import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

import {
  ProofTraceChangedError,
  exportProofTrace,
  type ProofTraceAssertion,
  type ProofTraceCitation,
  type ProofTraceExportFormat,
  type ProofTraceRequest,
} from "../../api/proofTrace";
import { useProofTrace } from "./useProofTrace";
import "./proof-trace.css";

export interface ProofTracePanelProps {
  request: ProofTraceRequest;
  onClose: () => void;
  onReturnFocus?: () => void;
  expectedSemanticDigest?: string | null;
  initialSourceLocator?: string | null;
}

const INITIAL_PATH_COUNT = 12;
const PATH_PAGE_SIZE = 20;
const INITIAL_ASSERTION_COUNT = 40;
const ASSERTION_PAGE_SIZE = 40;
const INITIAL_CITATION_COUNT = 80;
const CITATION_PAGE_SIZE = 80;
const SEMANTIC_CHILD_PAGE_SIZE = 50;

function humanize(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (letter: string) => letter.toLocaleUpperCase());
}

function shortIdentity(value: string): string {
  return value.length > 30 ? `${value.slice(0, 18)}…${value.slice(-8)}` : value;
}

function semanticPrimitive(value: unknown): string {
  if (value === null) return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function SemanticValueNode({ value, label }: { value: unknown; label: string }) {
  const groupId = useId();
  const [expanded, setExpanded] = useState(false);
  const [visibleChildren, setVisibleChildren] = useState(SEMANTIC_CHILD_PAGE_SIZE);
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === "object" && !isArray;
  const objectKeys = useMemo(() => (isObject ? Object.keys(value as Record<string, unknown>).sort() : []), [isObject, value]);
  const totalChildren = isArray ? value.length : objectKeys.length;

  if (!isArray && !isObject) {
    return (
      <li role="treeitem" className="proof-trace__semantic-leaf" data-proof-semantic-node>
        <span>{humanize(label)}</span><output>{semanticPrimitive(value)}</output>
      </li>
    );
  }
  if (totalChildren === 0) {
    return (
      <li role="treeitem" className="proof-trace__semantic-leaf" data-proof-semantic-node>
        <span>{humanize(label)}</span><output>{isArray ? "Empty list" : "Empty object"}</output>
      </li>
    );
  }

  const shown = expanded ? Math.min(visibleChildren, totalChildren) : 0;
  const children = [];
  if (expanded) {
    for (let index = 0; index < shown; index += 1) {
      const childKey = isArray ? String(index) : objectKeys[index]!;
      const childValue = isArray ? value[index] : (value as Record<string, unknown>)[childKey];
      children.push(
        <SemanticValueNode
          key={isArray ? index : childKey}
          value={childValue}
          label={isArray ? `Item ${index + 1}` : childKey}
        />,
      );
    }
  }
  const kind = isArray ? "items" : "fields";
  return (
    <li role="treeitem" aria-expanded={expanded} className="proof-trace__semantic-branch" data-proof-semantic-node>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={groupId}
        onClick={() => {
          setExpanded((current) => !current);
          if (expanded) setVisibleChildren(SEMANTIC_CHILD_PAGE_SIZE);
        }}
      >
        {expanded ? "Hide" : "Show"} {humanize(label)} · {totalChildren} {kind}
      </button>
      {expanded ? (
        <>
          <span className="proof-trace__semantic-progress" aria-live="polite">
            Showing {shown} of {totalChildren} {kind}
          </span>
          <ul id={groupId} role="group">
            {children}
          </ul>
          {shown < totalChildren ? (
            <button
              type="button"
              className="proof-trace__semantic-more"
              aria-label={`Show up to ${Math.min(SEMANTIC_CHILD_PAGE_SIZE, totalChildren - shown)} more ${kind}; ${shown} of ${totalChildren} visible`}
              onClick={() => setVisibleChildren((current) => Math.min(current + SEMANTIC_CHILD_PAGE_SIZE, totalChildren))}
            >
              Show more {kind}
            </button>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

function SemanticValueTree({ value, label }: { value: unknown; label: string }) {
  return (
    <ul className="proof-trace__semantic-tree" role="tree" aria-label={`${humanize(label)} semantic value`}>
      <SemanticValueNode value={value} label="Value" />
    </ul>
  );
}

function assertionTone(assertion: ProofTraceAssertion): "definition" | "invocation" | "semantic" {
  if (assertion.assertion_kind === "operation_spec_definition" || assertion.assertion_kind === "tool_snapshot") return "definition";
  if ([
    "operation_invocation", "operation_parameters", "operation_environment", "operation_assumptions",
  ].includes(assertion.assertion_kind)) return "invocation";
  return "semantic";
}

function citationIdentity(citation: ProofTraceCitation): string {
  switch (citation.citation_kind) {
    case "record": return `${humanize(citation.record_kind)} · ${citation.id}`;
    case "decision_evidence": return `Decision ${citation.decision_id} · evidence ${citation.ordinal}`;
    case "operation_registration_receipt": return `Operation ${citation.operation_id} · ${citation.idempotency_key}`;
    case "artifact_invalidation_receipt": return `ArtifactVersion ${citation.artifact_version_id} · ${citation.idempotency_key}`;
  }
}

function citationRecordedAt(citation: ProofTraceCitation): string {
  return citation.citation_kind === "record" ? "Canonical record" : citation.recorded_at;
}

function saveExport(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function ProofTracePanel({
  request,
  onClose,
  onReturnFocus,
  expectedSemanticDigest = null,
  initialSourceLocator = null,
}: ProofTracePanelProps) {
  const panelRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const { data, error, isLoading, refresh } = useProofTrace(request);
  const [exporting, setExporting] = useState<ProofTraceExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [visiblePathCount, setVisiblePathCount] = useState(INITIAL_PATH_COUNT);
  const [visibleAssertionCount, setVisibleAssertionCount] = useState(INITIAL_ASSERTION_COUNT);
  const [visibleCitationCount, setVisibleCitationCount] = useState(INITIAL_CITATION_COUNT);
  const [expandedPathIds, setExpandedPathIds] = useState<Set<string>>(() => new Set());
  const [focusedCitationId, setFocusedCitationId] = useState<string | null>(null);
  const documentValue = data?.document;
  const firstPathId = documentValue?.paths[0]?.path_id;
  const citationOrdinalById = useMemo(() => new Map(
    documentValue?.citations.map((citation, index) => [citation.citation_id, index]) ?? [],
  ), [documentValue]);
  const initialCitationId = useMemo(() => {
    const match = initialSourceLocator?.match(/^\/document\/citations\/(0|[1-9]\d*)$/);
    if (!match || !documentValue) return null;
    return documentValue.citations[Number(match[1])]?.citation_id ?? null;
  }, [documentValue, initialSourceLocator]);
  const citationPrefix = documentValue?.citations.slice(0, visibleCitationCount) ?? [];
  const focusedCitationIndex = focusedCitationId ? citationOrdinalById.get(focusedCitationId) : undefined;
  const focusedCitation = focusedCitationIndex !== undefined && focusedCitationIndex >= citationPrefix.length
    ? documentValue?.citations[focusedCitationIndex] ?? null
    : null;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    setVisiblePathCount(INITIAL_PATH_COUNT);
    setVisibleAssertionCount(INITIAL_ASSERTION_COUNT);
    setVisibleCitationCount(INITIAL_CITATION_COUNT);
    setExpandedPathIds(new Set(firstPathId ? [firstPathId] : []));
    setFocusedCitationId(initialCitationId);
  }, [data?.semantic_identity.digest, firstPathId, initialCitationId]);

  useEffect(() => {
    if (!initialCitationId || !documentValue || !citationOrdinalById.has(initialCitationId)) return;
    focusCitation(initialCitationId);
  // focusCitation intentionally changes with each render; the digest makes the
  // exact loaded proof the stable trigger for this one cited locator.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationOrdinalById, data?.semantic_identity.digest, documentValue, initialCitationId]);

  const semanticDigestMismatch = Boolean(
    data && expectedSemanticDigest && data.semantic_identity.digest !== expectedSemanticDigest,
  );

  const close = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => onReturnFocus?.());
  }, [onClose, onReturnFocus]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
  };

  const focusMountedCitation = (citationId: string): boolean => {
    const target = [...(panelRef.current?.querySelectorAll<HTMLElement>("[data-proof-citation]") ?? [])]
      .find((element) => element.dataset.proofCitation === citationId);
    if (!target) return false;
    target?.focus();
    target?.scrollIntoView({ block: "nearest" });
    return true;
  };

  const focusCitation = (citationId: string) => {
    if (focusMountedCitation(citationId)) return;
    const citationIndex = citationOrdinalById.get(citationId);
    if (citationIndex === undefined) return;
    setFocusedCitationId(citationId);
    window.requestAnimationFrame(() => focusMountedCitation(citationId));
  };

  const setPathExpanded = (pathId: string, expanded: boolean) => {
    setExpandedPathIds((current) => {
      if (current.has(pathId) === expanded) return current;
      const next = new Set(current);
      if (expanded) next.add(pathId);
      else next.delete(pathId);
      return next;
    });
  };

  const download = async (format: ProofTraceExportFormat) => {
    if (!data || exporting) return;
    setExportError(null);
    setExporting(format);
    const snapshot = data.document.snapshot;
    try {
      const result = await exportProofTrace({
        project_id: snapshot.project_id,
        root_ref: snapshot.root_ref,
        as_of: snapshot.effective_as_of,
        recorded_as_of: snapshot.recorded_as_of,
        snapshot_token: snapshot.snapshot_token,
        ...snapshot.limits,
        expected_semantic_digest: data.semantic_identity.digest,
      }, format);
      saveExport(result.blob, result.filename);
    } catch (exportFailure) {
      setExportError(exportFailure instanceof ProofTraceChangedError
        ? "Proof changed. Refresh it before downloading a new export."
        : exportFailure instanceof Error
          ? exportFailure.message
          : "Could not export this proof.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <aside
      ref={panelRef}
      className="proof-trace"
      role="complementary"
      aria-labelledby="proof-trace-title"
      onKeyDown={onKeyDown}
      data-testid="proof-trace-panel"
    >
      <header className="proof-trace__header">
        <div>
          <span>Regulator proof evidence</span>
          <h2 id="proof-trace-title">Proof trace</h2>
        </div>
        <button ref={closeRef} type="button" onClick={close} aria-label="Close proof trace">×</button>
      </header>

      {isLoading ? <div className="proof-trace__state" role="status">Assembling exact proof…</div> : null}
      {error ? (
        <div className="proof-trace__state is-error" role="alert">
          <strong>Proof unavailable</strong><span>{error.message}</span><button type="button" onClick={refresh}>Try again</button>
        </div>
      ) : null}
      {semanticDigestMismatch ? (
        <div className="proof-trace__state is-error" role="alert">
          <strong>Cited proof changed</strong><span>The reopened proof does not match the authorized source digest.</span>
        </div>
      ) : null}

      {documentValue && data && !semanticDigestMismatch ? (
        <>
          <div className="proof-trace__body">
            {documentValue.completeness.redaction_terminal ? (
              <p className="proof-trace__redaction" role="status">
                Restricted evidence was omitted. No hidden identity or count is disclosed.
              </p>
            ) : null}

            <section className="proof-trace__snapshot" aria-labelledby="proof-snapshot-heading">
              <h3 id="proof-snapshot-heading">Exact snapshot</h3>
              <div className="proof-trace__completeness" aria-label="Proof completeness">
                <span className={`is-${documentValue.completeness.traversal}`}>
                  <strong>Traversal</strong> {humanize(documentValue.completeness.traversal)}
                </span>
                <span className={`is-${documentValue.completeness.evidence}`}>
                  <strong>Evidence</strong> {humanize(documentValue.completeness.evidence)}
                </span>
              </div>
              <dl>
                <div><dt>ArtifactVersion</dt><dd title={documentValue.snapshot.root_ref.id}>{shortIdentity(documentValue.snapshot.root_ref.id)}</dd></div>
                <div><dt>Effective / event as of</dt><dd>{documentValue.snapshot.effective_as_of}</dd></div>
                <div><dt>Recorded / system as of</dt><dd>{documentValue.snapshot.recorded_as_of}</dd></div>
                <div><dt>Snapshot token</dt><dd title={documentValue.snapshot.snapshot_token}>{shortIdentity(documentValue.snapshot.snapshot_token)}</dd></div>
                <div><dt>Temporal basis</dt><dd>Effective and recorded time</dd></div>
                <div><dt>Bounds</dt><dd>{documentValue.snapshot.limits.max_depth} deep · {documentValue.snapshot.limits.max_records} records · {documentValue.snapshot.limits.max_paths} paths · {documentValue.snapshot.limits.max_path_steps} total steps</dd></div>
              </dl>
            </section>

            {documentValue.completeness.gaps.length ? (
              <section className="proof-trace__gaps" aria-labelledby="proof-gaps-heading">
                <h3 id="proof-gaps-heading">Incomplete evidence</h3>
                <p>The proof states every known gap explicitly; absence is never presented as certainty.</p>
                <ol>
                  {documentValue.completeness.gaps.map((gap) => (
                    <li key={`${gap.code}:${gap.subject_citation_id ?? ""}`}>
                      <strong>{humanize(gap.code)}</strong>
                      {gap.subject_citation_id ? <button type="button" onClick={() => focusCitation(gap.subject_citation_id!)}>View affected citation</button> : <span>Whole proof</span>}
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            <section aria-labelledby="proof-paths-heading">
              <div className="proof-trace__section-heading"><h3 id="proof-paths-heading">Ordered provenance paths</h3><span>{documentValue.paths.length}</span></div>
              <p className="proof-trace__progress" aria-live="polite">
                Showing {Math.min(visiblePathCount, documentValue.paths.length)} of {documentValue.paths.length} paths
              </p>
              <ol className="proof-trace__paths">
                {documentValue.paths.slice(0, visiblePathCount).map((path) => {
                  const expanded = expandedPathIds.has(path.path_id);
                  return (
                    <li key={path.path_id}>
                      <details open={expanded} onToggle={(event) => setPathExpanded(path.path_id, event.currentTarget.open)}>
                        <summary tabIndex={0}>Path {path.ordinal + 1} · {humanize(path.terminal.terminal_kind)}</summary>
                        {expanded ? (
                          <>
                            <ol className="proof-trace__steps">
                              {path.steps.map((step) => (
                                <li key={`${path.path_id}:${step.ordinal}`}>
                                  <span>{humanize(step.step_role)}</span>
                                  <button type="button" title={step.citation_id} onClick={() => focusCitation(step.citation_id)}>{shortIdentity(step.citation_id)}</button>
                                  <small>{step.assertion_ids.length} assertion{step.assertion_ids.length === 1 ? "" : "s"}</small>
                                </li>
                              ))}
                            </ol>
                            <p className="proof-trace__terminal"><strong>Terminal:</strong> {humanize(path.terminal.reason)}</p>
                          </>
                        ) : null}
                      </details>
                    </li>
                  );
                })}
              </ol>
              {visiblePathCount < documentValue.paths.length ? (
                <button className="proof-trace__load-more" type="button" onClick={() => setVisiblePathCount((current) => Math.min(current + PATH_PAGE_SIZE, documentValue.paths.length))}>
                  Load more paths
                </button>
              ) : null}
            </section>

            <section aria-labelledby="proof-assertions-heading">
              <div className="proof-trace__section-heading"><h3 id="proof-assertions-heading">Semantic assertions</h3><span>{documentValue.assertions.length}</span></div>
              <p className="proof-trace__progress" aria-live="polite">
                Showing {Math.min(visibleAssertionCount, documentValue.assertions.length)} of {documentValue.assertions.length} assertions
              </p>
              <ol className="proof-trace__assertions">
                {documentValue.assertions.slice(0, visibleAssertionCount).map((assertion) => {
                  const tone = assertionTone(assertion);
                  return (
                    <li key={`${data.semantic_identity.digest}:${assertion.assertion_id}`} className={`is-${tone}`}>
                      <header>
                        <div><span>{tone === "semantic" ? "Semantic fact" : humanize(tone)}</span><h4>{humanize(assertion.assertion_kind)}</h4></div>
                        <button type="button" onClick={() => focusCitation(assertion.subject_citation_id)}>Subject citation</button>
                      </header>
                      <div className="proof-trace__assertion-value">
                        <SemanticValueTree value={assertion.value} label={assertion.assertion_kind} />
                      </div>
                      <footer>
                        <span>Support</span>
                        {assertion.supporting_citation_ids.map((citationId) => (
                          <button type="button" key={citationId} title={citationId} onClick={() => focusCitation(citationId)}>{shortIdentity(citationId)}</button>
                        ))}
                      </footer>
                    </li>
                  );
                })}
              </ol>
              {visibleAssertionCount < documentValue.assertions.length ? (
                <button className="proof-trace__load-more" type="button" onClick={() => setVisibleAssertionCount((current) => Math.min(current + ASSERTION_PAGE_SIZE, documentValue.assertions.length))}>
                  Load more assertions
                </button>
              ) : null}
            </section>

            <section aria-labelledby="proof-citations-heading">
              <div className="proof-trace__section-heading"><h3 id="proof-citations-heading">Allow-listed citations</h3><span>{documentValue.citations.length}</span></div>
              <p className="proof-trace__progress" aria-live="polite">
                Showing {citationPrefix.length + (focusedCitation ? 1 : 0)} of {documentValue.citations.length} citations
              </p>
              <div className="proof-trace__table-wrap">
                <table>
                  <thead><tr><th scope="col">Citation</th><th scope="col">Kind</th><th scope="col">Visible identity</th><th scope="col">Recorded</th></tr></thead>
                  <tbody>
                    {citationPrefix.map((citation) => (
                      <tr key={citation.citation_id} tabIndex={-1} data-proof-citation={citation.citation_id}>
                        <th scope="row" title={citation.citation_id}>{shortIdentity(citation.citation_id)}</th>
                        <td>{humanize(citation.citation_kind)}</td>
                        <td>{citationIdentity(citation)}</td>
                        <td>{citationRecordedAt(citation)}</td>
                      </tr>
                    ))}
                    {focusedCitation && focusedCitationIndex !== undefined ? (
                      <>
                        <tr className="proof-trace__citation-gap">
                          <td colSpan={4}>{focusedCitationIndex - citationPrefix.length} citations remain collapsed before the focused citation at position {focusedCitationIndex + 1}.</td>
                        </tr>
                        <tr key={focusedCitation.citation_id} tabIndex={-1} data-proof-citation={focusedCitation.citation_id}>
                          <th scope="row" title={focusedCitation.citation_id}>{shortIdentity(focusedCitation.citation_id)}</th>
                          <td>{humanize(focusedCitation.citation_kind)}</td>
                          <td>{citationIdentity(focusedCitation)}</td>
                          <td>{citationRecordedAt(focusedCitation)}</td>
                        </tr>
                      </>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {visibleCitationCount < documentValue.citations.length ? (
                <button className="proof-trace__load-more" type="button" onClick={() => setVisibleCitationCount((current) => Math.min(current + CITATION_PAGE_SIZE, documentValue.citations.length))}>
                  Load more citations
                </button>
              ) : null}
            </section>
          </div>

          <footer className="proof-trace__footer">
            <div><span>Displayed semantic digest</span><code title={data.semantic_identity.digest}>{shortIdentity(data.semantic_identity.digest)}</code></div>
            {exportError ? <p role="alert">{exportError} <button type="button" onClick={() => { setExportError(null); refresh(); }}>Refresh proof</button></p> : null}
            <div className="proof-trace__exports">
              <button type="button" disabled={exporting !== null} onClick={() => void download("json")}>{exporting === "json" ? "Preparing JSON…" : "Download JSON"}</button>
              <button type="button" disabled={exporting !== null} onClick={() => void download("html")}>{exporting === "html" ? "Preparing print view…" : "Printable HTML"}</button>
            </div>
          </footer>
        </>
      ) : null}
    </aside>
  );
}
